<template>
    <div v-if="!showAdminControls && scrapedProject">
        <primeview-button v-if="confirmedMatchProject" text @click="openProjectInModal(confirmedMatchProject)">{{ confirmedMatchProject?.projectName }}</primeview-button>
        <div v-else>{{ scrapedProject.projectName }}</div>
    </div>
    <div class="flex-row-with-gap" v-else-if="scrapedProject">
        <div>
            <p :class="{ unmatched: scrapedProject.confirmedMatch === null && !scrapedProject.newProjectToAdd }">SCRAPED PROJECT TITLE: {{ scrapedProject.projectName }}</p>
            <p v-if="scrapedProject.confirmedMatch">Confirmed Match:<primeview-button text @click="openProjectInModal(confirmedMatchProject)">{{ confirmedMatchProject?.projectName }}</primeview-button></p>
            <p v-if="scrapedProject.newProjectToAdd">NEW PROJECT</p>

            <table>
                <tbody>
                    <tr><th>Country code</th><td>{{ scrapedProject.countryCode }}</td></tr>
                    <tr><th>Location detail</th><td>{{ convertLocationDetailToString(scrapedProject.locationNameDetail) }}</td></tr>
                    <tr><th>Latitude</th><td>{{ scrapedProject.latitude }}</td></tr>
                    <tr><th>Longitude</th><td>{{ scrapedProject.longitude }}</td></tr>
                    <tr><th>Segment</th><td>{{ scrapedProject.segment }}</td></tr>
                    <tr><th>Installation</th><td>{{ scrapedProject.installation }}</td></tr>
                    <tr><th>Technology</th><td>{{ scrapedProject.technology }}</td></tr>
                    <tr><th>Financers</th><td>{{ scrapedProject.financer?.join(', ') }}</td></tr>
                    <tr><th>Technology provider</th><td>{{ scrapedProject.technologyProvider?.join(', ') }}</td></tr>
                    <tr><th>Developers</th><td>{{ scrapedProject.developer?.join(', ') }}</td></tr>
                    <tr><th>Owners</th><td>{{ scrapedProject.owner?.join(', ') }}</td></tr>
                    <tr><th>Traders</th><td>{{ scrapedProject.trader?.join(', ') }}</td></tr>
                    <tr><th>Operators</th><td>{{ scrapedProject.operator?.join(', ') }}</td></tr>
                    <tr><th>Power capacity</th><td><template v-if="scrapedProject.powerCapacity">{{ scrapedProject.powerCapacity }} MW</template></td></tr>
                    <tr><th>Storage capacity</th><td><template v-if="scrapedProject.storageCapacity">{{ scrapedProject.storageCapacity }} MWh</template></td></tr>
                    <template v-for="(statusDate, index) in scrapedProject.statusArray" :key="index">
                        <tr><th>Status {{ index + 1 }}</th><td>{{ statusDate.status }} - {{ statusDate.date }}</td></tr>
                    </template>
                    <template v-if="!scrapedProject.statusArray?.length">
                        <tr><th>Status</th><td>NO STATUS IN DB</td></tr>
                    </template>
                </tbody>
            </table>
            <p />
            <div class="button-holder">
                <primeview-button class="plain-btn" @click="editDataModalVisible = true">Edit project data</primeview-button>
                <primeview-button class="plain-btn" @click="removeProjectData(props.article, props.index)">Remove project data</primeview-button>
                <primeview-button class="plain-btn" @click="markProjectAsNew(props.article, props.index)">Mark as new project to add</primeview-button>
            </div>
        </div>
        <div>
            <template v-if="!scrapedProject.confirmedMatch && scrapedProject.potentialMatches && scrapedProject.potentialMatches.length > 0">
                <div v-for="(potentialMatch, potentialMatchIndex) in scrapedProject.potentialMatches" :key="potentialMatchIndex">
                    <potential-match-project
                        :matched-project-data="getMatchingProject(potentialMatch.id)"
                        :potential-match-metadata="potentialMatch"
                        @open-project-in-modal="openProjectInModal"
                        @confirm-match="project => confirmMatch(props.article, props.index, project.factStorageId?.toString())"
                    />
                </div>
            </template>
            <div class="different-project-options">
                <label v-if="scrapedProject.confirmedMatch">Change linked project</label>
                <label v-else>Choose a different project:</label>
                <div class="button-holder">
                    <primevue-select
                        class="different-project-selector"
                        :options="allDataStore.allRedactedDataUnfiltered.filteredProjects"
                        filter
                        optionLabel="projectName"
                        :virtualScrollerOptions="{ itemSize: 38 }"
                        v-model="selectedMatchProject"
                    />
                    <primeview-button
                        :disabled="!selectedMatchProject"
                        class="plain-btn"
                        @click="confirmMatch(props.article, props.index, selectedMatchProject?.factStorageId?.toString())"
                    >
                        Confirm match
                    </primeview-button>
                </div>
            </div>
        </div>
    </div>
    <central-modal v-model:visible="editDataModalVisible" v-if="scrapedProject" :header="scrapedProject?.projectName ?? 'SCRAPED PROJECT IS MISSING'">
        <edit-scraped-data :project-info="scrapedProject" @cancel="editDataModalVisible = false" @update="updateScrapedProjectInfo(props.article, $event)" />
    </central-modal>
</template>

<script lang="ts" setup>
import PrimeviewButton from 'primevue/button';
import PrimevueSelect from 'primevue/select';
import { ref, computed } from 'vue';
import { IModifySpecificInfoPostObject } from '@/interfaces/storageTracking/IModifyInfoPostObject.ts';
import { IArticle, IProjectInfo, ILocationNameDetail } from '@/interfaces/storageTracking/IArticle.ts';
import { articleScrapingApi } from '@/api/storageTracking/ArticleScrapingApi.ts';
import { useAllDataStore } from '@/stores/storageTracking/AllDataStore.ts';
import { useToast } from 'primevue/usetoast';
import { IRedactedProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';
import CentralModal from '@/components/common/CentralModal.vue';
import EditScrapedData from './EditScrapedData.vue';
import PotentialMatchProject from './PotentialMatchProject.vue';

const props = defineProps<{
    article: IArticle;
    index: number;
    showAdminControls: boolean;
}>();

const emit = defineEmits<{
    'open-project-in-modal': [IRedactedProjectData];
    'update': [IProjectInfo];
}>();

const toast = useToast();
const allDataStore = useAllDataStore();

function updateScrapedProjectInfo (article: IArticle, updatedProjectInfo: IProjectInfo): void {
    if (!article.specificInfo) return;
    article.specificInfo[props.index] = updatedProjectInfo;

    const modifiedSpecificInfo: IModifySpecificInfoPostObject = { id: article.id, modifiedSpecificInfo: article.specificInfo };

    articleScrapingApi.modifySpecificInfo(modifiedSpecificInfo).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to remove project data' });
    });
    editDataModalVisible.value = false;
}

const selectedMatchProject = ref<IRedactedProjectData | null>();

const editDataModalVisible = ref<boolean>(false);

const scrapedProject = computed<IProjectInfo | undefined>(() => props.article.specificInfo?.[props.index]);

const confirmedMatchProject = computed<IRedactedProjectData | null>(() => {
    if (!scrapedProject.value?.confirmedMatch) return null;
    return getMatchingProject(scrapedProject.value.confirmedMatch);
});

function convertLocationDetailToString (locationDetail: ILocationNameDetail | undefined): string {
    if (!locationDetail) return '';
    return [locationDetail.otherIdentifiers, locationDetail.city, locationDetail.region, locationDetail.country]
        .filter(x => x)
        .join(', ');
}

function openProjectInModal (projectToOpen: IRedactedProjectData | null): void {
    if (!projectToOpen) {
        toast.add({ severity: 'error', life: 10000, summary: 'No project to open' });
        return;
    }
    emit('open-project-in-modal', projectToOpen);
}

function getMatchingProject (id: string): IRedactedProjectData | null {
    return allDataStore.allRedactedDataUnfiltered.filteredProjects.find(item => item.factStorageId?.toString() === id) ?? null;
}

async function removeProjectData (article: IArticle, index: number): Promise<void> {
    if (article.specificInfo == null) {
        toast.add({ severity: 'error', life: 10000, summary: 'Project info on article is null' });
        return;
    }
    article.specificInfo?.splice(index, 1);
    const modifiedSpecificInfo: IModifySpecificInfoPostObject = { id: article.id, modifiedSpecificInfo: article.specificInfo };

    articleScrapingApi.modifySpecificInfo(modifiedSpecificInfo).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to remove project data' });
    });
}

async function markProjectAsNew (article: IArticle, index: number): Promise<void> {
    if (article.specificInfo == null) {
        toast.add({ severity: 'error', life: 10000, summary: 'Project info on article is null' });
        return;
    }
    if (!article.specificInfo[index].projectName) {
        toast.add({ severity: 'error', life: 10000, summary: 'Please add a project name first' });
        return;
    }
    article.specificInfo[index].newProjectToAdd = true;
    article.specificInfo[index].confirmedMatch = null;
    article.specificInfo[index].potentialMatches = [];
    const modifiedSpecificInfo: IModifySpecificInfoPostObject = { id: article.id, modifiedSpecificInfo: article.specificInfo };

    articleScrapingApi.modifySpecificInfo(modifiedSpecificInfo).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to mark as new project' });
    });
}

async function confirmMatch (article: IArticle, projectIndex: number, confirmedMatch: string | undefined): Promise<void> {
    if (article.specificInfo == null) {
        toast.add({ severity: 'error', life: 10000, summary: 'Project info on article is null' });
        return;
    }
    if (!article.specificInfo[projectIndex].projectName) {
        toast.add({ severity: 'error', life: 10000, summary: 'Please add a project name first' });
        return;
    }
    if (confirmedMatch === undefined) {
        toast.add({ severity: 'error', life: 10000, summary: 'Confirmed match is undefined' });
        return;
    }

    article.specificInfo[projectIndex].confirmedMatch = confirmedMatch;
    article.specificInfo[projectIndex].potentialMatches = [];
    const modifiedSpecificInfo: IModifySpecificInfoPostObject = { id: article.id, modifiedSpecificInfo: article.specificInfo };

    articleScrapingApi.modifySpecificInfo(modifiedSpecificInfo).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to confirm match' });
    });
    selectedMatchProject.value = undefined;
}
</script>

<style lang="scss" scoped>
.unmatched {
    color: var(--lcpWatermelon);
}
</style>
